// STEP MOA-LIVE-VOICE-AUTO-WHISPER-1 · 자동 음성 제어 어댑터.
// VAD 구간 → 기존 Whisper (voice-auto/transcribe) → matchWakeWord → 기존 Claude 분류 → 기존 dispatchIntent.
// STEP MOA-LIVE-VOICE-AUTO-SAFETY-1 · 세션 generation guard · AbortController · 화면 숨김/이탈 시 종료.
import { useCallback, useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { matchWakeWord, WAKE_AWAIT_MS } from '@/lib/ramoWakeWordConfig';
import createAutoVad from '@/lib/ramoAutoVoice';

const LIMIT_CODES = ['daily_requests', 'daily_seconds', 'monthly_seconds', 'surge'];
const MAX_FAILS = 3;
const ZERO_COUNTS = { sent: 0, done: 0, failed: 0, cancelled: 0, blocked: 0, dropped: 0, stale: 0 };

const _sha256 = async (blob) => {
  try {
    const buf = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
    return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch { return ''; }
};

const _isCancel = (e) => e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError' || e?.name === 'AbortError';

const useRamoAutoVoice = ({ voice, isTestMode = false, getBroadcastStream = null } = {}) => {
  const [on, setOn] = useState(false);
  const [phase, setPhase] = useState('off');
  const [usage, setUsage] = useState(null);
  const [diag, setDiag] = useState({ lastTranscript: '', lastMatch: null, source: '', log: [], level: 0, frames: 0, ctxState: 'none', counts: ZERO_COUNTS });
  const [mockText, setMockTextState] = useState('');

  const vadRef = useRef(null);
  const voiceRef = useRef(voice);
  voiceRef.current = voice;
  const onRef = useRef(false);
  const startingRef = useRef(false);
  const genRef = useRef(0);
  const abortRef = useRef(null);
  const busyRef = useRef(false);
  const pendingRef = useRef(null);
  const awaitUntilRef = useRef(0);
  const awaitTimerRef = useRef(null);
  const failRef = useRef(0);
  const lastHashRef = useRef('');
  const mockQueueRef = useRef([]);

  const log = useCallback((line) => {
    const t = new Date().toLocaleTimeString();
    setDiag((d) => ({ ...d, log: [`${t} ${line}`, ...d.log].slice(0, 14) }));
  }, []);
  const count = useCallback((k) => {
    setDiag((d) => ({ ...d, counts: { ...d.counts, [k]: (d.counts?.[k] || 0) + 1 } }));
  }, []);
  const _alive = (gen) => gen === genRef.current && onRef.current;

  const setMockText = useCallback((v) => {
    setMockTextState(v);
    mockQueueRef.current = String(v || '').split('|').map((s) => s.trim()).filter(Boolean);
  }, []);

  const _clearAwait = () => {
    awaitUntilRef.current = 0;
    if (awaitTimerRef.current) { clearTimeout(awaitTimerRef.current); awaitTimerRef.current = null; }
  };

  // 멱등 · 자동 감지가 소유한 자원만 정리 (방송 트랙·LiveKit 미접촉).
  const stop = useCallback((nextPhase = 'off', reason = '') => {
    const wasActive = onRef.current || startingRef.current || !!vadRef.current;
    genRef.current += 1;
    onRef.current = false;
    startingRef.current = false;
    try { abortRef.current?.abort(); } catch {}
    abortRef.current = null;
    _clearAwait();
    if (pendingRef.current) { pendingRef.current = null; count('dropped'); }
    try { vadRef.current?.stop(); } catch {}
    vadRef.current = null;
    setOn(false);
    setPhase(nextPhase);
    if (reason && wasActive) log(reason);
  }, [log, count]);

  const _enterAwait = useCallback((gen) => {
    _clearAwait();
    awaitUntilRef.current = Date.now() + WAKE_AWAIT_MS;
    setPhase('awaiting');
    log('호출어 인식 · 명령 대기');
    awaitTimerRef.current = setTimeout(() => {
      awaitUntilRef.current = 0;
      if (!_alive(gen)) return;
      if (!busyRef.current) setPhase('idle');
      log('명령 대기 시간 초과 · 호출 대기 복귀');
    }, WAKE_AWAIT_MS);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [log]);

  const _exec = useCallback(async (text, gen) => {
    setPhase('exec');
    try {
      const r = await api.post('/seller/live/voice-auto/classify', { text }, { signal: abortRef.current?.signal });
      if (!_alive(gen)) { count('stale'); log('종료 후 도착한 명령 응답 · 실행 안 함'); return; }
      const intent = r.data?.intent || 'unknown';
      if (intent === 'unknown' || intent === 'wake') { log(`명령 해석 불가(${intent}) · 조작 없음`); return; }
      log(`명령 실행: ${intent} ${JSON.stringify(r.data?.params || {})}`);
      await voiceRef.current?.dispatchIntent?.(intent, r.data?.params || {});
    } catch (e) {
      if (_isCancel(e)) { count('cancelled'); log('명령 분류 요청 취소'); return; }
      log(`명령 분류 실패: ${e?.response?.status || e?.message}`);
    } finally {
      if (_alive(gen)) setPhase('idle');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [log, count]);

  const _process = useCallback(async (seg, gen) => {
    const hash = await _sha256(seg.blob);
    if (!_alive(gen)) { count('dropped'); return; }
    if (hash && hash === lastHashRef.current) { count('dropped'); log('중복 구간 · 전송 안 함'); return; }
    lastHashRef.current = hash;
    const fd = new FormData();
    fd.append('audio', seg.blob, 'auto.wav');
    if (isTestMode && mockQueueRef.current.length) {
      fd.append('mock_transcript', mockQueueRef.current.shift());
      setMockTextState(mockQueueRef.current.join(' | '));
    }
    setPhase('stt');
    count('sent');
    let r;
    try {
      r = await api.post('/seller/live/voice-auto/transcribe', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }, timeout: 15000, signal: abortRef.current?.signal,
      });
    } catch (e) {
      if (_isCancel(e)) { count('cancelled'); log('전송 후 취소 · 서버 처리/과금은 발생할 수 있음'); return; }
      const code = e?.response?.data?.detail?.code || '';
      if (code) count('blocked');
      if (!_alive(gen)) return;
      if (LIMIT_CODES.includes(code)) { stop('limited', `사용량 제한 도달(${code}) · 자동 모드 중지 · PTT 유지`); return; }
      if (code) { log(`전송 차단: ${code}`); setPhase(awaitUntilRef.current ? 'awaiting' : 'idle'); return; }
      count('failed');
      failRef.current += 1;
      log(`Whisper 요청 실패 (${failRef.current}/${MAX_FAILS}) · 재시도 안 함`);
      if (failRef.current >= MAX_FAILS) { stop('error', '연속 실패 · 자동 모드 중지'); return; }
      setPhase('idle');
      return;
    }
    count('done');
    if (r.data?.usage) setUsage(r.data.usage);
    if (!_alive(gen)) { count('stale'); log('종료 후 도착한 전사 · 무시'); return; }
    failRef.current = 0;
    const transcript = r.data?.transcript || '';
    const m = matchWakeWord(transcript);
    setDiag((d) => ({ ...d, lastTranscript: `${transcript}${r.data?.mock ? ' (mock)' : ''}`, lastMatch: { matched: m.matched, alias: m.alias, command: m.command } }));
    if (awaitUntilRef.current > Date.now()) {
      const cmd = m.matched ? m.command : transcript;
      _clearAwait();
      if (!cmd) { _enterAwait(gen); return; }
      await _exec(cmd, gen);
      return;
    }
    if (!m.matched) { log('호출어 없음 · 무시'); setPhase('idle'); return; }
    if (m.command) { await _exec(m.command, gen); return; }
    _enterAwait(gen);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTestMode, log, count, stop, _exec, _enterAwait]);

  const _onSegment = useCallback(async (seg, gen = genRef.current) => {
    if (!_alive(gen)) { count('dropped'); return; }
    if (busyRef.current) {
      if (pendingRef.current) count('dropped');
      pendingRef.current = seg; log('처리 중 · 다음 구간 1개 대기'); return;
    }
    busyRef.current = true;
    try { await _process(seg, gen); } finally {
      busyRef.current = false;
      const next = pendingRef.current; pendingRef.current = null;
      if (next) _onSegment(next, gen);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_process, log, count]);

  const start = useCallback(async () => {
    if (onRef.current || startingRef.current || !voiceRef.current?.enabled) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    startingRef.current = true;
    const gen = ++genRef.current;
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;
    try {
      const u = await api.get('/seller/live/voice-auto/usage', { signal });
      if (gen !== genRef.current) return;
      setUsage(u.data);
      if (u.data?.blocked) { startingRef.current = false; setPhase('limited'); log(`사용량 제한 도달(${u.data.blocked}) · PTT 를 사용하세요`); return; }
    } catch (e) {
      if (gen === genRef.current) { startingRef.current = false; if (!_isCancel(e)) log('사용량 조회 실패'); }
      return;
    }
    let stream = getBroadcastStream?.() || null;
    let own = false;
    if (!stream) {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 } });
        own = true;
      } catch {
        if (gen === genRef.current) { startingRef.current = false; setPhase('error'); log('마이크를 사용할 수 없어요'); }
        return;
      }
      if (gen !== genRef.current) { try { stream.getTracks().forEach((t) => t.stop()); } catch {} return; }
    }
    const vad = createAutoVad();
    try {
      await vad.start({
        stream, ownStream: own,
        onSpeechStart: () => { if (_alive(gen) && !busyRef.current) setPhase('speech'); },
        onSpeechEnd: () => { if (_alive(gen) && !busyRef.current) setPhase(awaitUntilRef.current ? 'awaiting' : 'idle'); },
        onSegment: (seg) => _onSegment(seg, gen),
      });
    } catch {
      vad.stop();
      if (gen === genRef.current) stop('error', '오디오 파이프라인 실패');
      return;
    }
    if (gen !== genRef.current) { vad.stop(); return; }
    vadRef.current = vad;
    onRef.current = true;
    startingRef.current = false;
    failRef.current = 0;
    setDiag((d) => ({ ...d, source: own ? '전용 마이크' : '방송 마이크 재사용(읽기 전용)' }));
    setOn(true);
    setPhase('idle');
    log('자동 음성 제어 ON');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getBroadcastStream, log, stop, _onSegment]);

  const toggle = useCallback(() => {
    (onRef.current || startingRef.current) ? stop('off', '자동 음성 제어 OFF') : start();
  }, [start, stop]);

  useEffect(() => {
    if (!voice?.enabled) stop('off', '음성 명령 OFF → 자동 제어 중지');
  }, [voice?.enabled, stop]);

  // 화면 숨김(백그라운드/다른 앱) → 일시 중단 · 복귀 시 자동 재시작 없음. 이탈/새로고침/탭 종료 → 종료.
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'hidden') stop('paused', '화면 숨김 → 자동 감지 일시 중단 (재개는 직접 ON)'); };
    const onLeave = () => stop('off', '페이지 이탈 → 자동 감지 종료');
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pagehide', onLeave);
    window.addEventListener('beforeunload', onLeave);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pagehide', onLeave);
      window.removeEventListener('beforeunload', onLeave);
    };
  }, [stop]);

  useEffect(() => {
    if (!on) return undefined;
    const t = setInterval(() => {
      const v = vadRef.current?.getDiag?.();
      if (v) setDiag((d) => ({ ...d, level: v.level, frames: v.frames, ctxState: v.ctxState, dropShort: v.dropShort }));
    }, 600);
    return () => clearInterval(t);
  }, [on]);

  // unmount (스튜디오 이탈 · 뒤로가기) — 동일 stop 경로.
  const stopRef = useRef(stop);
  stopRef.current = stop;
  useEffect(() => () => { stopRef.current('off'); }, []);

  return { on, phase, usage, diag, toggle, start, stop, mockText, setMockText, isTestMode };
};

export default useRamoAutoVoice;
